import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import client from '../api/client';
import CollapsiblePanel from './CollapsiblePanel';
import './RegionMarketStatsTable.css';
import './RegionIskVolumeSection.css';

const RANGES = [[90, '90 days'], [180, '6 months'], [365, '1 year']];
const AVERAGES = [[7, 'avg7', '7-day avg', 'var(--series-1)'], [30, 'avg30', '30-day avg', 'var(--series-2)']];
const LONGEST_AVERAGE = 30;

const compactIsk = new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 2 }).format;
const shortDate = (iso) => new Date(`${iso}T00:00:00Z`).toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' });
const mean = (vals) => (vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null);

// Daily ISK plus trailing averages; incomplete days are left blank and out of the averages
export function buildRows(days, rangeDays) {
  const isk = days.map(d => (d.complete ? d.isk_volume : null));
  const rows = days.map((d, i) => {
    const row = { date: d.date, isk: isk[i] };
    AVERAGES.forEach(([n, key]) => {
      row[key] = isk[i] == null ? null : mean(isk.slice(Math.max(0, i - n + 1), i + 1).filter(v => v != null));
    });
    return row;
  });
  // Extra days were fetched only to warm up the averages
  return rows.slice(Math.max(0, rows.length - rangeDays));
}

// Last 30 complete days' average against the 30 before them
export function recentChange(days) {
  const isk = days.filter(d => d.complete).map(d => d.isk_volume);
  if (isk.length < 2 * LONGEST_AVERAGE) return null;
  const recent = mean(isk.slice(-LONGEST_AVERAGE));
  const prior = mean(isk.slice(-2 * LONGEST_AVERAGE, -LONGEST_AVERAGE));
  return prior ? (recent - prior) / prior : null;
}

// path: an endpoint returning { days: [{ date, isk_volume, complete }] }
export default function RegionIskVolumeSection({ path, title = 'Region ISK volume', defaultCollapsed = true }) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const [rangeDays, setRangeDays] = useState(180);
  const [excludePlex, setExcludePlex] = useState(true);
  const [days, setDays] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await client.get(path, {
        params: { days: rangeDays + LONGEST_AVERAGE, exclude_plex: excludePlex ? 1 : 0 },
      });
      setDays(response.data.days);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load region ISK volume');
    } finally {
      setLoading(false);
    }
  }, [path, rangeDays, excludePlex]);

  useEffect(() => {
    if (!collapsed) load();
  }, [collapsed, load]);

  const rows = useMemo(() => (days ? buildRows(days, rangeDays) : []), [days, rangeDays]);
  const change = useMemo(() => (days ? recentChange(days) : null), [days]);
  const incomplete = days ? days.slice(-rangeDays).filter(d => !d.complete).length : 0;

  return (
    <CollapsiblePanel
      title={title}
      sub={change != null && (
        <span className={change < 0 ? 'riv-change riv-change--down' : 'riv-change riv-change--up'}>
          Last 30 days {change >= 0 ? '+' : ''}{(change * 100).toFixed(1)}% vs previous 30
        </span>
      )}
      collapsed={collapsed}
      onToggle={() => setCollapsed(!collapsed)}
      actions={(
        <>
          <label className="riv-toggle">
            <input type="checkbox" checked={excludePlex} onChange={e => setExcludePlex(e.target.checked)} />
            Exclude PLEX items
          </label>
          <select
            className="region-days-selector"
            value={rangeDays}
            onChange={e => setRangeDays(Number(e.target.value))}
            aria-label="Time window"
          >
            {RANGES.map(([d, label]) => <option key={d} value={d}>Last {label}</option>)}
          </select>
        </>
      )}
    >
      <div className="riv-root">
        {error && <div className="error-message">{error}</div>}
        {incomplete > 0 && (
          <p className="riv-note">
            {incomplete} day{incomplete === 1 ? ' is' : 's are'} missing most of the region's types and
            {incomplete === 1 ? ' is' : ' are'} left off the chart. The daily market history fetch fills them in.
          </p>
        )}
        {loading && days === null ? (
          <p className="region-overview-empty">Loading…</p>
        ) : rows.length ? (
          <ResponsiveContainer width="100%" height={320}>
            <ComposedChart data={rows} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--line)" vertical={false} />
              <XAxis dataKey="date" tickFormatter={shortDate} tick={{ fill: 'var(--text-dim)', fontSize: 11 }} minTickGap={24} />
              <YAxis tickFormatter={compactIsk} tick={{ fill: 'var(--text-dim)', fontSize: 11 }} width={70} />
              <Tooltip
                contentStyle={{ backgroundColor: 'var(--surface)', border: '1px solid var(--line-strong)', borderRadius: 6 }}
                labelStyle={{ color: 'var(--text-dim)' }}
                itemStyle={{ color: 'var(--text)' }}
                labelFormatter={shortDate}
                formatter={(value, name) => [value == null ? '—' : `${compactIsk(value)} ISK`, name]}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} formatter={label => <span className="riv-legend">{label}</span>} />
              <Bar dataKey="isk" name="Daily ISK" fill="var(--line-strong)" radius={[2, 2, 0, 0]} isAnimationActive={false} />
              {AVERAGES.map(([, key, label, color]) => (
                <Line key={key} dataKey={key} name={label} stroke={color} strokeWidth={2} dot={false} isAnimationActive={false} />
              ))}
            </ComposedChart>
          </ResponsiveContainer>
        ) : (
          !loading && <p className="region-overview-empty">No market history for this region yet.</p>
        )}
      </div>
    </CollapsiblePanel>
  );
}
